import type { ResidenceData } from '@repo/sdk/gen'
import { Alert, AlertDescription } from '@repo/ui/alert'
import { Button } from '@repo/ui/button'
import { DetailSection } from '@repo/ui/components/detail-section'
import { InputCurrency } from '@repo/ui/components/input-currency'
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel
} from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import { Spinner } from '@repo/ui/spinner'
import { Textarea } from '@repo/ui/textarea'
import { formatError } from '@repo/ui/utils'
import {
	BedDouble,
	type LucideIcon,
	Percent,
	TriangleAlert
} from 'lucide-react'
import { type ReactNode, useId } from 'react'
import {
	Controller,
	FormProvider,
	useForm,
	useFormContext,
	useFormState,
	useWatch
} from 'react-hook-form'
import { toast } from 'sonner'
import i18n, { type TKey, useTranslation } from '@/lib/i18n'
import { useSeats } from '../-hooks/use-seats'
import {
	messagesOf,
	printableErrorsOf,
	residenceErrorsOf
} from '../-lib/residence-errors'
import {
	type ResidenceFormData,
	residenceResolver
} from '../-lib/residence-form'
import { InstitutionPicker } from './institution-picker'
import { RequiredMark } from './required-mark'
import { ServicePicker } from './service-picker'
import { FirstSiteFields } from './sites-fields'

type IntegerName =
	| 'q30SingleBeds'
	| 'q30DoubleBeds'
	| 'q70SingleBeds'
	| 'q70DoubleBeds'
type NullableIntegerName = 'fundedBeds' | 'contractSigningDays'
type CentsName = 'q70SingleRateCents' | 'q70DoubleRateCents'
type TextName = 'name'
type NullableTextName =
	| 'operatorLegalName'
	| 'grantDecree'
	| 'dpoContact'
	| 'infoEmail'
	| 'pecAddress'
	| 'callLinkUrl'
	| 'contractSigningInstructions'
	| 'depositInstructions'
	| 'iban'
	| 'bankAccountHolder'

interface ResidenceFormProps {
	defaultValues: ResidenceFormData
	/** Read-only: every field disabled and no save button. */
	readOnly?: boolean
	/** Hide the save bar, e.g. while a tab without residence data is open. */
	hideActions?: boolean
	submitLabel: string
	/** The groups the page shows as tabs, in order: a refused save names the tab of each problem. */
	tabs?: readonly ResidenceFieldGroup[]
	/** Throws the API error; the form shows validation errors on their fields. */
	onSubmit: (data: ResidenceFormData) => Promise<unknown>
	/** The field groups below, laid out by the page: stacked or in tabs. */
	children: ReactNode
}

/**
 * The residence data in the groups of the flows document (3.3), each field
 * with the control of its type. The form values are the API shape, validated
 * by the SDK schema: one definition of every field type, from the database to
 * the input. Business checks (quota 30%, rates, IBAN) run on the API and land
 * on their field with the threshold or the rate. The page
 * lays the groups out; the `<form>` element wraps only the save bar, so a
 * dialog with its own form inside the page never submits this one. The bar is
 * pinned to the bottom of the scrolling page: it stays in reach on any tab.
 */
function ResidenceForm({
	defaultValues,
	readOnly = false,
	hideActions = false,
	submitLabel,
	tabs,
	onSubmit,
	children
}: ResidenceFormProps) {
	const { t } = useTranslation()
	const form = useForm<ResidenceFormData>({
		resolver: residenceResolver,
		defaultValues,
		disabled: readOnly
	})

	// The page stays on its tab: the problems of all of them are said here, each with its tab and field.
	const showProblems = (problems: { path: string[]; message: string }[]) => {
		const lines = [
			...new Set(
				problems.map(({ path, message }) =>
					problemLine(path, message, tabs)
				)
			)
		]
		toast.error(t('admin-residences:form.fix_fields'), {
			description: (
				<ul className='flex list-disc flex-col gap-1 pl-4'>
					{lines.slice(0, MAX_LINES).map((line) => (
						<li key={line}>{line}</li>
					))}
				</ul>
			),
			duration: 12_000
		})
	}

	const submit = async (values: ResidenceFormData) => {
		try {
			await onSubmit(values)
		} catch (error) {
			const printable = printableErrorsOf(error)
			if (printable) {
				showProblems(printable)
				return
			}
			const fieldErrors = residenceErrorsOf(error, values.sites)
			if (fieldErrors) {
				for (const item of fieldErrors) {
					form.setError(item.field, { message: item.message })
				}
				showProblems(
					fieldErrors.map((item) => ({
						path: item.field.split('.'),
						message: item.message
					}))
				)
				return
			}
			toast.error(formatError(error))
		}
	}

	return (
		<FormProvider {...form}>
			<div className='flex flex-col gap-8'>
				{children}
				{!(readOnly || hideActions) && (
					<form
						className='sticky bottom-0 -mx-4 -mb-4 flex justify-end border-border border-t bg-background px-4 py-3 shadow-md md:-mx-6 md:-mb-6 md:px-6'
						noValidate
						onSubmit={form.handleSubmit(submit, (errors) =>
							showProblems(messagesOf(errors))
						)}
					>
						<Button
							disabled={form.formState.isSubmitting}
							type='submit'
						>
							{form.formState.isSubmitting && <Spinner />}
							{submitLabel}
						</Button>
					</form>
				)}
			</div>
		</FormProvider>
	)
}

/** The fields of each group, to show which group holds an error. */
const GROUP_FIELDS = {
	identity: [
		'name',
		'operatorLegalName',
		'grantDecree',
		'sites',
		'pecAddress',
		'dpoContact',
		'infoEmail',
		'admittedInstitutions'
	],
	general: [
		'name',
		'operatorLegalName',
		'grantDecree',
		'callLinkUrl',
		'dpoContact',
		'admittedInstitutions'
	],
	sites: ['sites'],
	services: ['services'],
	seats: [
		'fundedBeds',
		'q30SingleBeds',
		'q30DoubleBeds',
		'q70SingleBeds',
		'q70DoubleBeds',
		'q70SingleRateCents',
		'q70DoubleRateCents'
	],
	contract: [
		'contractSigningDays',
		'contractSigningInstructions',
		'depositInstructions',
		'iban',
		'bankAccountHolder'
	],
	pec: ['pecAddress']
} as const satisfies Record<string, readonly (keyof ResidenceData)[]>

type ResidenceFieldGroup = keyof typeof GROUP_FIELDS

/** A dot on a tab whose group holds a field error. */
function GroupErrorDot({ group }: { group: ResidenceFieldGroup }) {
	const { t } = useTranslation()
	const { errors } = useFormState<ResidenceData>({
		name: [...GROUP_FIELDS[group]]
	})
	// The Anagrafica asks only the address of the first site: the rest of the
	// sites has its own tab.
	const firstSite = errors.sites?.[0]
	const hasError = GROUP_FIELDS[group].some((field) =>
		field === 'sites' && group === 'identity'
			? firstSite?.name || firstSite?.address || firstSite?.city
			: errors[field]
	)
	if (!hasError) {
		return null
	}
	return (
		<span className='size-1.5 rounded-full bg-destructive'>
			<span className='sr-only'>
				{t('admin-residences:form.tab_has_errors')}
			</span>
		</span>
	)
}

/**
 * The facility manager's first step, the "Anagrafica" of the data sheet: who
 * the residence is and its grant decree, where it is, the mailbox the
 * applications reach and the universities the call names.
 */
function IdentityFields() {
	const { t } = useTranslation()
	return (
		<FormFields>
			<TextField name='name' required />
			<NullableTextField name='operatorLegalName' required />
			<NullableTextField
				description={t('admin-residences:hints.grantDecree')}
				name='grantDecree'
			/>
			<FirstSiteFields />
			<NullableTextField
				description={t('admin-residences:hints.pecAddress')}
				name='pecAddress'
				required
				type='email'
			/>
			<NullableTextField
				description={t('admin-residences:hints.dpoContact')}
				name='dpoContact'
				required
			/>
			<NullableTextField
				description={t('admin-residences:hints.infoEmail')}
				name='infoEmail'
				type='email'
			/>
			<InstitutionsField required />
		</FormFields>
	)
}

/** Name, operator, grant decree, call link, data protection officer, address for information, admitted institutions. */
function GeneralFields() {
	const { t } = useTranslation()
	return (
		<FormFields>
			<TextField name='name' />
			<NullableTextField name='operatorLegalName' />
			<NullableTextField
				description={t('admin-residences:hints.grantDecree')}
				name='grantDecree'
			/>
			<NullableTextField name='callLinkUrl' type='url' />
			<NullableTextField
				description={t('admin-residences:hints.dpoContact')}
				name='dpoContact'
			/>
			<InstitutionsField />
		</FormFields>
	)
}

/** The services the residence offers, ticked from the closed list. */
function ServicesFields() {
	return (
		<FormFields>
			<ServicePicker />
		</FormFields>
	)
}

/** The DSU rate the general admin sets for every residence, in euro cents; null until set. */
interface DsuRate {
	single: number | null
	double: number | null
}

/**
 * The seats of the sites divided between the quotas, with the rates: the
 * quota 30% charges the DSU rate, which the general admin sets for every
 * residence and is shown here, and the quota 70% charges the rates the manager
 * decides.
 */
function SeatsFields({ dsuRate }: { dsuRate: DsuRate }) {
	const { t } = useTranslation()
	return (
		<>
			<FormSection
				icon={BedDouble}
				title={t('admin-residences:groups.beds')}
			>
				<SeatsSummary />
				<OverSitesNotice />
				<NullableIntegerField
					description={t('admin-residences:hints.fundedBeds')}
					name='fundedBeds'
				/>
			</FormSection>
			<div className='grid items-start gap-8 lg:grid-cols-2'>
				<Quota30Section dsuRate={dsuRate} />
				<Quota70Section />
			</div>
		</>
	)
}

/** The seats the manager gives the quota 30%, each room type beside its DSU rate. */
function Quota30Section({ dsuRate }: { dsuRate: DsuRate }) {
	const { t } = useTranslation()
	return (
		<FormSection
			icon={Percent}
			title={t('admin-residences:groups.quota30')}
		>
			{(dsuRate.single === null || dsuRate.double === null) && (
				<p className='text-muted-foreground text-sm md:col-span-2'>
					{t('admin-residences:references.dsu_missing')}
				</p>
			)}
			<IntegerField
				label={t('admin-residences:seat_fields.single_beds')}
				name='q30SingleBeds'
			/>
			<DsuRateValue
				label={t('admin-residences:seat_fields.single_rate')}
				value={dsuRate.single}
			/>
			<IntegerField
				description={<DoubleRooms />}
				label={t('admin-residences:seat_fields.double_beds')}
				name='q30DoubleBeds'
			/>
			<DsuRateValue
				label={t('admin-residences:seat_fields.double_rate')}
				value={dsuRate.double}
			/>
		</FormSection>
	)
}

/** What the sites leave to the quota 70%, each room type beside the rate the manager decides. */
function Quota70Section() {
	const { t } = useTranslation()
	const seats = useSeats()
	return (
		<FormSection
			icon={Percent}
			title={t('admin-residences:groups.quota70')}
		>
			<DerivedSeats
				label={t('admin-residences:seat_fields.single_beds')}
				name='q70SingleBeds'
				value={seats.q70Single}
			/>
			<CentsField
				label={t('admin-residences:seat_fields.single_rate')}
				name='q70SingleRateCents'
				required={seats.q70Single > 0}
			/>
			<DerivedSeats
				label={t('admin-residences:seat_fields.double_beds')}
				name='q70DoubleBeds'
				value={seats.q70Double}
			/>
			<CentsField
				label={t('admin-residences:seat_fields.double_rate')}
				name='q70DoubleRateCents'
				required={seats.q70Double > 0}
			/>
		</FormSection>
	)
}

/** A rate the page shows and the manager does not write. */
function DsuRateValue({
	label,
	value
}: {
	label: string
	value: number | null
}) {
	const id = useId()
	return (
		<Field>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			{/* The same field as the rates the manager writes, read only. */}
			<InputCurrency
				className='bg-muted/50'
				currencies={['EUR']}
				name={id}
				onChange={() => undefined}
				readOnly
				value={{
					amount: value === null ? '' : String(value),
					currency: 'EUR'
				}}
			/>
		</Field>
	)
}

/** Signing terms, deposit, bank account and the address for information: what the Avviso and the offer say that changes from residence to residence. Every one is written into the call. */
function ContractFields() {
	const { t } = useTranslation()
	// Not marked as needed: the call does not name them, the offers do not start without them.
	return (
		<FormFields>
			<NullableIntegerField
				description={t('admin-residences:hints.contractSigningDays')}
				name='contractSigningDays'
			/>
			<NullableTextField name='bankAccountHolder' />
			<NullableTextField
				autoCapitalize='characters'
				maxLength={34}
				name='iban'
			/>
			<NullableTextField multiline name='contractSigningInstructions' />
			<NullableTextField multiline name='depositInstructions' />
		</FormFields>
	)
}

/** The PEC address the applications are sent to. */
function PecFields() {
	const { t } = useTranslation()
	return (
		<FormFields>
			<NullableTextField
				description={t('admin-residences:hints.pecAddress')}
				name='pecAddress'
				type='email'
			/>
		</FormFields>
	)
}

/**
 * The fields of a tab that holds one group: no title, because the tab already
 * names it.
 */
function FormFields({ children }: { children: ReactNode }) {
	return (
		<FieldGroup className='grid gap-x-6 gap-y-5 md:grid-cols-2'>
			{children}
		</FieldGroup>
	)
}

function FormSection({
	icon,
	title,
	children
}: {
	icon: LucideIcon
	title: string
	children: ReactNode
}) {
	return (
		<DetailSection icon={icon} title={title}>
			<FieldGroup className='grid gap-x-6 gap-y-5 md:grid-cols-2'>
				{children}
			</FieldGroup>
		</DetailSection>
	)
}

/** Label, control, description and error of one field. */
function FieldShell({
	name,
	label,
	description,
	wide = false,
	required = false,
	error,
	children
}: {
	name: keyof ResidenceData
	/** Replaces the field's own label where the section already names the quota. */
	label?: string
	description?: ReactNode
	wide?: boolean
	/** Marks a datum the call is written from. */
	required?: boolean
	error?: { message?: string }
	children: ReactNode
}) {
	const { t } = useTranslation()
	return (
		<Field
			className={wide ? 'md:col-span-2' : undefined}
			data-invalid={error !== undefined}
		>
			<FieldLabel
				className={required ? 'gap-1' : undefined}
				htmlFor={`residence-${name}`}
			>
				{label ?? t(`admin-residences:fields.${name}`)}
				{required && <RequiredMark />}
			</FieldLabel>
			{children}
			{description && <FieldDescription>{description}</FieldDescription>}
			{error && <FieldError errors={[error]} />}
		</Field>
	)
}

function TextField({
	name,
	description,
	required
}: {
	name: TextName
	description?: ReactNode
	required?: boolean
}) {
	const { control } = useFormContext<ResidenceData>()
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<FieldShell
					description={description}
					error={fieldState.error}
					name={name}
					required={required}
				>
					<Input
						{...field}
						aria-invalid={fieldState.invalid}
						disabled={field.disabled}
						id={`residence-${name}`}
					/>
				</FieldShell>
			)}
		/>
	)
}

/** Optional text: an empty input is `null`, never an empty string. */
function NullableTextField({
	name,
	label,
	description,
	multiline = false,
	wide = false,
	required,
	...inputProps
}: {
	name: NullableTextName
	label?: string
	description?: ReactNode
	multiline?: boolean
	wide?: boolean
	required?: boolean
	type?: 'email' | 'url'
	inputMode?: 'numeric'
	maxLength?: number
	autoCapitalize?: 'characters'
}) {
	const { control } = useFormContext<ResidenceData>()
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => {
				const controlProps = {
					ref: field.ref,
					name: field.name,
					disabled: field.disabled,
					onBlur: field.onBlur,
					id: `residence-${name}`,
					'aria-invalid': fieldState.invalid,
					value: field.value ?? '',
					onChange: (event: { target: { value: string } }) =>
						field.onChange(
							event.target.value.trim() === ''
								? null
								: event.target.value
						)
				}
				return (
					<FieldShell
						description={description}
						error={fieldState.error}
						label={label}
						name={name}
						required={required}
						wide={multiline || wide}
					>
						{multiline ? (
							<Textarea {...controlProps} rows={3} />
						) : (
							<Input {...controlProps} {...inputProps} />
						)}
					</FieldShell>
				)
			}}
		/>
	)
}

/** A whole number; an empty input is not a number and the schema says so. */
/** Seats the form works out and shows, never asks. */
function DerivedSeats({
	name,
	label,
	value
}: {
	name: IntegerName
	label: string
	value: number
}) {
	return (
		<FieldShell label={label} name={name}>
			<Input
				className='bg-muted/50 tabular-nums'
				id={`residence-${name}`}
				readOnly
				value={Math.max(value, 0)}
			/>
		</FieldShell>
	)
}

function IntegerField({
	name,
	label,
	description
}: {
	name: IntegerName
	label?: string
	description?: ReactNode
}) {
	const { control } = useFormContext<ResidenceData>()
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<FieldShell
					description={description}
					error={fieldState.error}
					label={label}
					name={name}
				>
					<Input
						aria-invalid={fieldState.invalid}
						className='tabular-nums'
						disabled={field.disabled}
						id={`residence-${name}`}
						inputMode='numeric'
						min={0}
						name={field.name}
						onBlur={field.onBlur}
						onChange={(event) =>
							field.onChange(event.target.valueAsNumber)
						}
						ref={field.ref}
						type='number'
						value={Number.isNaN(field.value) ? '' : field.value}
					/>
				</FieldShell>
			)}
		/>
	)
}

/** An optional whole number: an empty input is `null`. */
function NullableIntegerField({
	name,
	description,
	required
}: {
	name: NullableIntegerName
	description?: ReactNode
	required?: boolean
}) {
	const { control } = useFormContext<ResidenceData>()
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<FieldShell
					description={description}
					error={fieldState.error}
					name={name}
					required={required}
				>
					<Input
						aria-invalid={fieldState.invalid}
						className='tabular-nums'
						disabled={field.disabled}
						id={`residence-${name}`}
						inputMode='numeric'
						min={0}
						name={field.name}
						onBlur={field.onBlur}
						onChange={(event) =>
							field.onChange(
								event.target.value === ''
									? null
									: event.target.valueAsNumber
							)
						}
						ref={field.ref}
						type='number'
						value={field.value ?? ''}
					/>
				</FieldShell>
			)}
		/>
	)
}

/** A monthly rate in euro, held as integer cents; empty is no rate. */
function CentsField({
	name,
	label,
	description,
	required
}: {
	name: CentsName
	label?: string
	description?: ReactNode
	required?: boolean
}) {
	const { control } = useFormContext<ResidenceData>()
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState }) => (
				<FieldShell
					description={description}
					error={fieldState.error}
					label={label}
					name={name}
					required={required}
				>
					<InputCurrency
						aria-invalid={fieldState.invalid}
						currencies={['EUR']}
						disabled={field.disabled}
						min={0}
						// InputCurrency uses the name as the input id the label points to.
						name={`residence-${name}`}
						onBlur={field.onBlur}
						onChange={(value) =>
							field.onChange(
								value.amount === ''
									? null
									: Number(value.amount)
							)
						}
						ref={field.ref}
						value={{
							amount:
								field.value === null ? '' : String(field.value),
							currency: 'EUR'
						}}
					/>
				</FieldShell>
			)}
		/>
	)
}

/** The universities whose students may apply, searched in the catalogue. */
function InstitutionsField({ required }: { required?: boolean }) {
	const { t } = useTranslation()
	const { control } = useFormContext<ResidenceData>()
	const city =
		useWatch<ResidenceData, 'sites.0.city'>({ name: 'sites.0.city' }) ?? ''
	return (
		<Controller
			control={control}
			name='admittedInstitutions'
			render={({ field, fieldState }) => (
				<FieldShell
					description={t(
						'admin-residences:hints.admittedInstitutions'
					)}
					error={fieldState.error}
					name='admittedInstitutions'
					required={required}
					wide
				>
					<InstitutionPicker
						city={city}
						disabled={field.disabled}
						id='residence-admittedInstitutions'
						invalid={fieldState.invalid}
						onChange={field.onChange}
						value={field.value}
					/>
				</FieldShell>
			)}
		/>
	)
}

/**
 * Double-room seats as rooms, while the admin types. A double room may hold a
 * DSU seat and a TMR one, so each quota can be odd: only both together fill
 * whole rooms.
 */
function DoubleRooms() {
	const { t } = useTranslation()
	const { double } = useSeats()
	if (double <= 0) {
		return t('admin-residences:hints.double_beds')
	}
	return double % 2 === 0
		? t('admin-residences:hints.double_rooms', { count: double / 2 })
		: t('admin-residences:hints.double_beds_odd')
}

/** The quota 30% asks for more seats than the sites have: it saves, but the call waits. */
function OverSitesNotice() {
	const { t } = useTranslation()
	const seats = useSeats()
	if (seats.q70Single >= 0 && seats.q70Double >= 0) {
		return null
	}
	return (
		<Alert className='md:col-span-2' variant='warning'>
			<TriangleAlert />
			<AlertDescription>
				{t('admin-residences:hints.q30_over_sites', {
					single: seats.single,
					double: seats.double
				})}
			</AlertDescription>
		</Alert>
	)
}

/** The seats of the sites and the share the quota 30% takes, while the admin types. */
function SeatsSummary() {
	const { t, i18n } = useTranslation()
	const seats = useSeats()
	const q30 = seats.q30Single + seats.q30Double
	const total = seats.single + seats.double
	return (
		<p className='text-muted-foreground text-sm tabular-nums md:col-span-2'>
			{t('admin-residences:hints.seats_summary', {
				total,
				single: seats.single,
				double: seats.double,
				q30,
				// Cut, not rounded: 59 of 200 is 29,5%, not the 30% the quota asks.
				percent: new Intl.NumberFormat(i18n.language, {
					maximumFractionDigits: 1
				}).format(total > 0 ? Math.floor((q30 * 1000) / total) / 10 : 0)
			})}
		</p>
	)
}

const MAX_LINES = 5

const FIELD_LABEL: Record<string, TKey> = {
	admittedInstitutions: 'admin-residences:fields.admittedInstitutions',
	bankAccountHolder: 'admin-residences:fields.bankAccountHolder',
	callLinkUrl: 'admin-residences:fields.callLinkUrl',
	contractSigningDays: 'admin-residences:fields.contractSigningDays',
	contractSigningInstructions:
		'admin-residences:fields.contractSigningInstructions',
	depositInstructions: 'admin-residences:fields.depositInstructions',
	dpoContact: 'admin-residences:fields.dpoContact',
	fundedBeds: 'admin-residences:fields.fundedBeds',
	grantDecree: 'admin-residences:fields.grantDecree',
	iban: 'admin-residences:fields.iban',
	infoEmail: 'admin-residences:fields.infoEmail',
	name: 'admin-residences:fields.name',
	operatorLegalName: 'admin-residences:fields.operatorLegalName',
	pecAddress: 'admin-residences:fields.pecAddress',
	q30DoubleBeds: 'admin-residences:fields.q30DoubleBeds',
	q30SingleBeds: 'admin-residences:fields.q30SingleBeds',
	q70DoubleRateCents: 'admin-residences:fields.q70DoubleRateCents',
	q70SingleRateCents: 'admin-residences:fields.q70SingleRateCents',
	services: 'admin-residences:fields.services'
}

const SITE_FIELD_LABEL: Record<string, TKey> = {
	address: 'admin-residences:site_fields.address',
	bathroomsCount: 'admin-residences:site_fields.bathroomsCount',
	bathroomsDescription: 'admin-residences:site_fields.bathroomsDescription',
	city: 'admin-residences:site_fields.city',
	doubleBeds: 'admin-residences:site_fields.doubleBeds',
	equippedBathroomsDescription:
		'admin-residences:site_fields.equippedBathroomsDescription',
	equippedDoubleBeds: 'admin-residences:site_fields.equippedDoubleBeds',
	equippedKitchensDescription:
		'admin-residences:site_fields.equippedKitchensDescription',
	equippedOtherServicesDescription:
		'admin-residences:site_fields.equippedOtherServicesDescription',
	equippedSingleBeds: 'admin-residences:site_fields.equippedSingleBeds',
	kitchensCount: 'admin-residences:site_fields.kitchensCount',
	kitchensDescription: 'admin-residences:site_fields.kitchensDescription',
	name: 'admin-residences:site_fields.name',
	otherServicesDescription:
		'admin-residences:site_fields.otherServicesDescription',
	singleBeds: 'admin-residences:site_fields.singleBeds',
	typology: 'admin-residences:site_fields.typology'
}

const TAB_NAME = {
	identity: 'admin-residences:tabs.identity',
	general: 'admin-residences:tabs.general',
	sites: 'admin-residences:tabs.sites',
	services: 'admin-residences:tabs.services',
	seats: 'admin-residences:tabs.seats',
	contract: 'admin-residences:tabs.contract',
	pec: 'admin-residences:tabs.pec'
} as const satisfies Record<ResidenceFieldGroup, TKey>

/**
 * A problem said with its tab and its field, so it is found without
 * looking: "Sedi · Sede 1, Posti in doppia: ...". The message of a site's
 * problem that names the site already is not given the site again.
 */
function problemLine(
	path: string[],
	message: string,
	tabs?: readonly ResidenceFieldGroup[]
) {
	const [root = '', index, key = ''] = path
	// The Anagrafica asks only the address of the first site; the rest has its tab.
	const firstSiteAddress =
		index === '0' && ['name', 'address', 'city'].includes(key)
	const group = tabs?.find((candidate) =>
		root === 'sites'
			? candidate === 'sites' ||
				(candidate === 'identity' && firstSiteAddress)
			: (GROUP_FIELDS[candidate] as readonly string[]).includes(root)
	)
	const number = Number(index) + 1
	const namesSite =
		root === 'sites' && new RegExp(`sede ${number}\\b`, 'i').test(message)
	const fieldKey =
		root === 'sites' ? SITE_FIELD_LABEL[key] : FIELD_LABEL[root]
	const field = namesSite || !fieldKey ? undefined : i18n.t(fieldKey)
	const site =
		root === 'sites' && !namesSite && index !== undefined
			? i18n.t('admin-residences:sites.title', { number })
			: undefined
	const where = [
		group ? i18n.t(TAB_NAME[group]) : undefined,
		[site, field].filter(Boolean).join(', ') || undefined
	].filter(Boolean)
	return where.length > 0 ? `${where.join(' · ')}: ${message}` : message
}

export {
	ContractFields,
	type DsuRate,
	GeneralFields,
	GROUP_FIELDS,
	GroupErrorDot,
	IdentityFields,
	PecFields,
	ResidenceForm,
	type ResidenceFieldGroup,
	SeatsFields,
	ServicesFields
}
